import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Button, SearchField, SegmentedTabs } from "@akuna/ui";
import { Check, ChevronRight, Info, Pencil, UserPlus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useClearGroupItemRates, useMemberModels, useSetItemRate, useSetRate } from "@/hooks/useCommissions";
import { formatCurrency, formatPercent } from "@/lib/format/br";
import { commissionErrorKey } from "@/lib/commissions/api";
import type { GroupKind } from "@/lib/commissions/groups";
import { buildModelTree, commissionAmount, filterTree, isGroupRateMixed, type ModelGroupNode } from "@/lib/commissions/tree";
import type { CatalogGroup, CatalogItem, CommissionModel } from "@/lib/commissions/types";
import { getInitials } from "@/lib/utils/string-utils";
import { cn } from "@/lib/utils";
import { formatList } from "@/lib/format/list";
import { PercentCell } from "@/components/commissions/PercentCell";

const KINDS: GroupKind[] = ["service", "product", "package"];
const MAX_AVATARS = 4;

type Props = {
  model: CommissionModel;
  groups: CatalogGroup[];
  items: CatalogItem[];
  canEdit: boolean;
  onEdit: () => void;
  onAssign: () => void;
};

export function CommissionModelEditor({ model, groups, items, canEdit, onEdit, onAssign }: Props) {
  const { t, i18n } = useTranslation("commissions");
  const [kind, setKind] = useState<GroupKind>("service");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [isEditing, setIsEditing] = useState(false);
  const { data: members = [] } = useMemberModels();
  const setRate = useSetRate();
  const setItemRate = useSetItemRate();
  const clearItems = useClearGroupItemRates();

  const tree = useMemo(() => buildModelTree(kind, groups, items, model, i18n.language), [kind, groups, items, model, i18n.language]);
  const visible = useMemo(() => filterTree(tree, query), [tree, query]);
  const people = members.filter((m) => m.model_id === model.id);
  const keyOf = (n: ModelGroupNode) => n.groupId ?? "none";
  const allOpen = visible.length > 0 && visible.every((n) => open.has(keyOf(n)));
  const searching = query.trim() !== "";

  const run = async (p: Promise<void>, success = t("catalog.models.rateSaved")) => {
    try {
      await p;
      toast({ title: success });
    } catch (error) {
      toast({ title: t(commissionErrorKey(error)), variant: "destructive" });
    }
  };
  const toggle = (key: string) =>
    setOpen((s) => {
      const copy = new Set(s);
      if (copy.has(key)) copy.delete(key);
      else copy.add(key);
      return copy;
    });
  const pct = (v: number | null) => (v === null ? t("catalog.models.noRate") : formatPercent(v, i18n.language));

  return (
    <section aria-labelledby="commission-model-name" className="flex min-w-0 flex-[999_1_620px] flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-card border border-border bg-card px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 id="commission-model-name" className="flex flex-wrap items-center gap-2 font-display text-xl font-extrabold">
              {model.name}
              {!model.is_active && <Badge>{t("catalog.models.inactive")}</Badge>}
            </h2>
            <p className="text-sm text-muted-foreground">{t("basis.label")}: {t(`basis.${model.calculation_basis}`)}</p>
          </div>
          {canEdit && (
            <Button variant="outline" onClick={onEdit}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              {t("catalog.models.editModel")}
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-muted px-3.5 py-2.5">
          <div className="flex min-w-0 items-center gap-3">
            {people.length > 0 && (
              <div className="flex shrink-0 -space-x-2" aria-hidden="true">
                {people.slice(0, MAX_AVATARS).map((p) => (
                  <Avatar key={p.user_id} name={p.full_name} fallback={getInitials(p.full_name)} className="ring-2 ring-muted" />
                ))}
              </div>
            )}
            <p className="text-sm">
              {people.length === 0
                ? t("catalog.models.nobody")
                : t("catalog.models.usedByNames", { names: formatList(people.map((p) => p.full_name), i18n.language) })}
            </p>
          </div>
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={onAssign} className="text-primary">
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              {t("assign.open")}
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-card border border-border bg-card">
        <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-4 sm:px-5">
          <SegmentedTabs
            variant="outline"
            label={t("catalog.models.kindLabel")}
            value={kind}
            onValueChange={(v) => {
              setKind(v as GroupKind);
              setOpen(new Set());
            }}
            items={KINDS.map((k) => ({ value: k, label: t(`kinds.${k}`) }))}
          />
          <SearchField
            className="min-w-0 flex-[1_1_220px]"
            label={t("catalog.models.search")}
            placeholder={t("catalog.models.searchPlaceholder")}
            clearLabel={t("catalog.models.clearSearch")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery("")}
          />
          {!searching && visible.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-primary"
              onClick={() => setOpen(allOpen ? new Set() : new Set(visible.map(keyOf)))}
            >
              {allOpen ? t("catalog.models.collapseAll") : t("catalog.models.expandAll")}
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" className="text-primary" onClick={() => setIsEditing((editing) => !editing)}>
              {isEditing ? <Check className="h-4 w-4" aria-hidden="true" /> : <Pencil className="h-4 w-4" aria-hidden="true" />}
              {isEditing ? t("catalog.models.doneEditing") : t("catalog.models.editCommissions")}
            </Button>
          )}
        </div>

        <div className="hidden grid-cols-[minmax(0,1fr)_auto] gap-3 bg-muted px-5 py-2.5 text-xs font-medium text-muted-foreground sm:grid">
          <span>{t("catalog.models.groupOrItem")}</span>
          <span className="w-28 text-right">{t("catalog.models.percent")}</span>
        </div>

        {visible.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground">
            {searching ? t("catalog.models.noMatch") : t("catalog.models.noItems")}
          </p>
        ) : (
          visible.map((node) => {
            const key = keyOf(node);
            const isOpen = searching || open.has(key);
            const name = node.groupId ? node.name : t("catalog.models.noGroup");
            const panelId = `commission-group-${kind}-${key}`;
            const mixedRate = node.groupId !== null && isGroupRateMixed(node.rate, node.items);
            return (
              <div key={key} className="border-t border-border first:border-t-0 sm:first:border-t">
                <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-2 sm:px-3", !node.groupId && "bg-state-warning-soft/40")}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(key)}
                    className="flex min-h-12 min-w-0 flex-[1_1_220px] items-center gap-2.5 rounded-sm px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronRight
                      className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none", isOpen && "rotate-90")}
                      aria-hidden="true"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-semibold">{name}</span>
                      <span className="text-xs text-muted-foreground">
                        {node.groupId ? t("catalog.models.itemCount", { count: node.items.length }) : t("catalog.models.noGroupHint", { count: node.items.length })}
                      </span>
                    </span>
                    {node.groupId && node.ownCount > 0 && (
                      <Badge variant="warning" className="shrink-0">{t("catalog.models.ownCount", { count: node.ownCount })}</Badge>
                    )}
                  </button>
                  <div className="ml-auto flex items-center justify-end gap-2 pr-2">
                    {canEdit && isEditing && node.groupId && node.ownCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary"
                        onClick={() =>
                          run(clearItems.mutateAsync({ modelId: model.id, kind, groupId: node.groupId! }), t("catalog.models.appliedToAll", { group: node.name }))
                        }
                      >
                        {t("catalog.models.applyToAll")}
                      </Button>
                    )}
                    {node.groupId ? (
                      canEdit && isEditing ? (
                        <PercentCell
                          label={t("catalog.models.groupRateLabel", { group: node.name, model: model.name })}
                          placeholder="0"
                          value={node.rate}
                          onCommit={(percent) => run(setRate.mutateAsync({ modelId: model.id, kind, groupId: node.groupId!, percent }))}
                        />
                      ) : (
                        <span className="w-28 text-right tabular-nums">{mixedRate ? t("catalog.models.perItemRates") : pct(node.rate)}</span>
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">{t("catalog.models.itemByItem")}</span>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <ul id={panelId} className="pb-2">
                    {node.items.map((item) => {
                      const own = item.own !== null && node.groupId !== null;
                      return (
                        <li key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-border py-1.5 pl-10 pr-4 sm:pl-14 sm:pr-5">
                          <span className="flex min-w-0 flex-[1_1_180px] items-center gap-2 text-sm">
                            <span className="truncate">{item.name}</span>
                            {node.groupId && (own ? <Badge variant="warning">{t("catalog.models.own")}</Badge> : <Badge>{t("catalog.models.fromGroup")}</Badge>)}
                          </span>
                          <div className="ml-auto flex items-center justify-end gap-2">
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {t("catalog.models.saleValue")}: {formatCurrency(item.sale_price, i18n.language)}
                            </span>
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {t("catalog.models.commissionValue")}: {formatCurrency(commissionAmount(item.sale_price, item.effective), i18n.language)}
                            </span>
                            {canEdit && isEditing && own && (
                              <Button
                                variant="link"
                                size="sm"
                                className="min-h-11"
                                onClick={() => run(setItemRate.mutateAsync({ modelId: model.id, itemId: item.id, percent: null }))}
                              >
                                {node.rate === null ? t("catalog.models.backToGroup") : t("catalog.models.backTo", { value: pct(node.rate) })}
                              </Button>
                            )}
                            {canEdit && isEditing ? (
                              <PercentCell
                                label={t("catalog.models.itemRateLabel", { item: item.name, model: model.name })}
                                placeholder="0"
                                highlighted={own}
                                value={item.effective}
                                // Typing the group's own value back means "follow the group" again.
                                onCommit={(percent) =>
                                  run(setItemRate.mutateAsync({
                                    modelId: model.id, itemId: item.id,
                                    percent: node.groupId && percent === node.rate ? null : percent,
                                  }))
                                }
                              />
                            ) : (
                              <span className="w-28 text-right tabular-nums">{pct(item.effective)}</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })
        )}

        <p className="flex items-start gap-2.5 border-t border-border bg-muted px-5 py-3.5 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t("catalog.models.description")}
        </p>
      </div>
    </section>
  );
}
